import { Badge } from '@chakra-ui/react';

import { PLAN_IDS, type PlanIdValue } from '@fluenceur/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeCheck } from 'iconoir-react';

interface PlanBadgeProps {
  /**
   * Plan ID of the subscription
   */
  planId: PlanIdValue;
  /**
   * Size variant of the badge
   * @default 'xs'
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /**
   * Whether to show the icon
   * @default true
   */
  showIcon?: boolean;
}

/**
 * PlanBadge Component
 *
 * Displays a badge showing the current subscription plan (Essential/Growth).
 * Used in the UserMenu and Subscription settings page for consistent plan display.
 *
 * Features:
 * - Yellow color scheme for Essential plan
 * - Green color scheme for Growth plan
 * - Seal check icon to indicate active subscription
 * - Configurable size variants
 * - Translatable labels
 *
 * @example
 * ```tsx
 * <PlanBadge planId="essential_monthly" size="xs" />
 * <PlanBadge planId="growth_monthly" size="sm" showIcon={false} />
 * ```
 */
const PlanBadge: React.FC<PlanBadgeProps> = ({ planId, size = 'xs', showIcon = true }) => {
  const { t } = useTranslation();

  // Determine if this is a Growth plan
  const isGrowthPlan = planId === PLAN_IDS.GROWTH;
  const isFreePlan = planId === 'free-beta';

  // Get plan name based on plan ID
  const planName = isFreePlan
    ? t('pricing.plan.free.name')
    : isGrowthPlan
      ? t('settings.subscription.currentPlan.growth')
      : t('settings.subscription.currentPlan.essential');

  // Use different color scheme for Growth plan
  const colorPalette = isFreePlan ? 'purple' : isGrowthPlan ? 'blue' : 'yellow';

  return (
    <Badge colorPalette={colorPalette} size={size}>
      {showIcon && <BadgeCheck width={14} height={14} />}
      {planName}
    </Badge>
  );
};

export default PlanBadge;
